import { Button } from '@iris/ui/components/ui/button';
import { cn } from '@iris/ui/lib/utils';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * ADR 0011 D6: the back arrow of the top bar. It goes back in the app's own history when there is one (React Router
 * counts entries in `history.state.idx`), else to the parent screen, so a link opened from outside never leaves the
 * app. The arrow points the reading way back (flipped in Arabic).
 */
export function BackButton({
  label,
  fallback,
  className,
}: {
  label: string;
  fallback: string;
  className?: string;
}) {
  const navigate = useNavigate();
  const back = () => {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  };
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      onClick={back}
      className={cn('-ms-2 h-10 w-10 shrink-0 rounded-full', className)}
    >
      <ArrowLeft aria-hidden className="h-5 w-5 rtl:-scale-x-100" />
    </Button>
  );
}

/**
 * Where "back" leads from a path when there is no history: the path without its last part, mapped through `parents`
 * for screens whose parent is elsewhere (e.g. `/m/:id` → `/explore`). Null on a top-level screen (no arrow).
 */
export function parentPath(
  pathname: string,
  topLevel: readonly string[],
  parents: ReadonlyArray<readonly [RegExp, string]> = [],
): string | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (topLevel.includes(path)) return null;
  for (const [pattern, parent] of parents) if (pattern.test(path)) return parent;
  const up = path.slice(0, path.lastIndexOf('/')) || '/';
  return up;
}
